import { useEffect, useState, createContext, useContext, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Leaf,
  Droplets,
  Sprout,
  Scissors,
  Trees,
  Compass,
  ArrowUpRight,
  MapPin,
  Mail,
  Phone,
  Menu,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WHATSAPP_NUMBER_DISPLAY = "+39 388 193 7674";
const WHATSAPP_NUMBER_RAW = "393881937674";
const EMAIL_CONTACT = "giovannimichael94@hotmail.it";

// ---------------- WhatsApp prefill context ----------------
const WhatsAppCtx = createContext({
  service: "",
  setService: () => {},
  href: `https://wa.me/${WHATSAPP_NUMBER_RAW}`,
});

const useWhatsApp = () => useContext(WhatsAppCtx);

const buildWhatsAppHref = (service) => {
  const base = `https://wa.me/${WHATSAPP_NUMBER_RAW}`;
  let msg;
  if (!service) {
    msg = "Ciao Michael, sono interessato a un preventivo per il mio giardino a Pantelleria.";
  } else if (service === "altro") {
    msg = "Ciao Michael, sono interessato a un servizio personalizzato per il mio giardino a Pantelleria. Vorrei parlarne.";
  } else {
    const label = SERVICE_LABELS_PLAIN[service] || "un preventivo";
    msg = `Ciao Michael, sono interessato al servizio: ${label}. Sto valutando un preventivo per il mio giardino a Pantelleria.`;
  }
  return `${base}?text=${encodeURIComponent(msg)}`;
};

const PHOTOS = [
  {
    url: "https://customer-assets.emergentagent.com/job_progetto-giardino/artifacts/lxx018uq_IMG-20260505-WA0015.jpg",
    alt: "Giardino mediterraneo a Pantelleria con ulivi, bouganville fucsia e dammuso sullo sfondo",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_progetto-giardino/artifacts/zxesz92s_IMG-20260505-WA0015.jpg",
    alt: "Giardino mediterraneo con ulivi e bouganville fucsia",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_progetto-giardino/artifacts/xut0o5mj_IMG-20260505-WA0016.jpg",
    alt: "Vista mare con ulivi, bouganville e cactus a Pantelleria",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_progetto-giardino/artifacts/fbjtzck6_IMG-20260505-WA0017.jpg",
    alt: "Tramonto sul mare con palme — paesaggio pantesco",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_progetto-giardino/artifacts/7pbvpy8o_IMG-20260505-WA0009.jpg",
    alt: "Giardino mediterraneo con palme e bouganville",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_progetto-giardino/artifacts/ckrtxg8x_IMG-20260505-WA0008.jpg",
    alt: "Lavoro di piantumazione su muretti a secco",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_progetto-giardino/artifacts/355g0nbr_IMG-20260505-WA0012.jpg",
    alt: "Vista panoramica del giardino realizzato",
  },
];

const SERVICES = [
  {
    id: "progettazione_giardino",
    title: "Progettazione giardini",
    desc:
      "Studio degli spazi e scelta delle piante mediterranee.",
    longDesc:
      "Il servizio che ci distingue: ascolto il tuo dammuso o la tua villa, osservo esposizioni, vento di scirocco e muretti a secco, e disegno un giardino su misura con piante mediterranee autoctone — pensato per durare nel tempo e richiedere poca acqua.",
    icon: Compass,
    accent: "olive",
  },
  {
    id: "impianti_irrigazione",
    title: "Impianti di irrigazione",
    desc:
      "Sistemi efficienti calibrati per il clima dell'isola — ogni goccia conta, la tua oasi resta rigogliosa anche d'estate.",
    icon: Droplets,
    accent: "lava",
  },
  {
    id: "piantumazione_autoctone",
    title: "Piantumazione piante autoctone",
    desc:
      "Capperi, ulivi, palme, bouganville, fichi: specie tipiche pantesche che vivono in armonia con la pietra lavica.",
    icon: Sprout,
    accent: "terracotta",
  },
  {
    id: "manutenzione_giardino",
    title: "Manutenzione giardino",
    desc:
      "Potature, cura stagionale, controllo infestanti. Il tuo giardino sempre in forma, tutto l'anno.",
    icon: Scissors,
    accent: "lava",
  },
  {
    id: "fornitura_piante",
    title: "Fornitura piante",
    desc:
      "Reperisco e consegno piante adulte e giovani per dare carattere immediato al tuo dammuso.",
    icon: Trees,
    accent: "olive",
  },
  {
    id: "consulenza_sopralluogo",
    title: "Consulenza & Sopralluogo",
    desc:
      "Vengo da te, valutiamo insieme lo spazio, il sole, il vento. Un piano chiaro prima di iniziare.",
    icon: Leaf,
    accent: "terracotta",
  },
];

const SERVICE_LABELS = {
  progettazione_giardino: "Progettazione giardini",
  impianti_irrigazione: "Impianti di irrigazione",
  piantumazione_autoctone: "Piantumazione piante autoctone",
  manutenzione_giardino: "Manutenzione giardino",
  fornitura_piante: "Fornitura piante",
  consulenza_sopralluogo: "Consulenza / Sopralluogo",
  altro: "Altro (specifica sotto)",
};

const SERVICE_LABELS_PLAIN = {
  progettazione_giardino: "Progettazione giardini",
  impianti_irrigazione: "Impianti di irrigazione",
  piantumazione_autoctone: "Piantumazione piante autoctone",
  manutenzione_giardino: "Manutenzione giardino",
  fornitura_piante: "Fornitura piante",
  consulenza_sopralluogo: "Consulenza / Sopralluogo",
  altro: "servizio personalizzato",
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

// ---------------- NAV ----------------
const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { href: waHref } = useWhatsApp();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#chi-sono", label: "Chi sono" },
    { href: "#servizi", label: "Servizi" },
    { href: "#lavori", label: "Lavori" },
    { href: "#faq", label: "FAQ" },
    { href: "#preventivo", label: "Preventivo" },
    { href: "#contatti", label: "Contatti" },
  ];

  return (
    <header
      data-testid="nav-bar"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
        scrolled
          ? "bg-cream/85 backdrop-blur-xl border-b border-lava/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-12 h-16 md:h-20 flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <span className="w-9 h-9 bg-lava text-cream flex items-center justify-center font-display text-base font-medium tracking-tight">
            MG
          </span>
          <span className="hidden sm:flex flex-col leading-none">
            <span className="font-display text-base tracking-tight font-medium">
              Michael Guida
            </span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-lava/55 mt-1 font-mono">
              Giardiniere · Pantelleria
            </span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-7 lg:gap-9">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="editorial-link text-sm tracking-wide text-lava/80 hover:text-lava"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={waHref}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-whatsapp-btn"
            className="hidden sm:inline-flex items-center gap-2 bg-olive text-cream px-5 py-2.5 text-sm tracking-wide hover:bg-olive-deep transition-colors duration-300"
          >
            WhatsApp
            <ArrowUpRight className="w-4 h-4" />
          </a>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-lava"
            aria-label="Apri menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-cream border-t border-lava/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  className="font-display text-2xl tracking-[-0.03em] text-lava font-light"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={waHref}
                target="_blank"
                rel="noreferrer"
                data-testid="nav-mobile-whatsapp"
                className="inline-flex items-center justify-between bg-olive text-cream px-5 py-3 text-sm tracking-wide"
              >
                Scrivimi su WhatsApp
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

// ---------------- HERO ----------------
const Hero = () => {
  const { href: waHref } = useWhatsApp();
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 md:pt-36 pb-12 md:pb-24 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-12 grid grid-cols-12 gap-6 md:gap-8 items-end">
        {/* TEXT BLOCK */}
        <motion.div
          initial="hidden"
          animate="show"
          variants={{ show: { transition: { staggerChildren: 0.1 } } }}
          className="col-span-12 lg:col-span-7 relative z-10"
        >
          <motion.div variants={fadeUp} className="flex items-center gap-3 mb-8">
            <span className="w-2 h-2 bg-olive" />
            <span className="text-[11px] uppercase tracking-[0.25em] text-lava/60 font-mono">
              dal 2015 / Pantelleria
            </span>
          </motion.div>

          <motion.h1
            variants={fadeUp}
            data-testid="hero-title"
            className="font-display text-[15vw] sm:text-[12vw] md:text-[10vw] lg:text-[8.5vw] xl:text-[7.5vw] leading-[0.9] tracking-[-0.05em] font-extralight text-lava"
          >
            Giardiniere
            <br />
            <span className="font-medium text-olive">Pantelleria</span>
            <span className="text-terracotta">.</span>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            className="mt-10 max-w-xl text-base md:text-lg text-lava/70 leading-relaxed font-light"
          >
            Stai costruendo casa o una struttura ricettiva sull'isola? Sono
            Michael Guida. Progetto, piantumo e curo giardini mediterranei
            fatti di pietra lavica, palme e bouganville.
          </motion.p>

          <motion.div
            variants={fadeUp}
            className="mt-10 flex flex-wrap items-center gap-3"
          >
            <a
              href="#preventivo"
              data-testid="hero-cta-preventivo"
              className="group inline-flex items-center gap-3 bg-lava text-cream px-7 py-4 text-sm tracking-tight font-medium hover:bg-olive transition-colors duration-500"
            >
              Richiedi preventivo
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href={waHref}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-cta-whatsapp"
              className="inline-flex items-center gap-3 px-7 py-4 text-sm tracking-tight font-medium text-lava border border-lava/20 hover:border-lava hover:bg-lava hover:text-cream transition-all duration-500"
            >
              WhatsApp
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </motion.div>

          <motion.div
            variants={fadeUp}
            className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-2 text-[11px] uppercase tracking-[0.2em] text-lava/45 font-mono"
          >
            <span>· Privati</span>
            <span>· Aziende</span>
            <span>· Strutture ricettive</span>
          </motion.div>
        </motion.div>

        {/* IMAGE BLOCK */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
          className="col-span-12 lg:col-span-5 relative"
        >
          <div className="relative aspect-[4/5] overflow-hidden bg-sand">
            <img
              src={PHOTOS[0].url}
              alt={PHOTOS[0].alt}
              className="w-full h-full object-cover kenburns"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-lava/20 to-transparent" />
          </div>
          {/* Caption */}
          <div className="absolute -bottom-6 -left-6 md:-left-12 bg-cream border border-lava/15 px-5 py-3 max-w-[260px] hidden sm:block">
            <p className="text-[10px] uppercase tracking-[0.2em] text-lava/55 mb-1 font-mono">
              001 / Lavoro realizzato
            </p>
            <p className="font-display text-base text-lava font-medium tracking-tight">
              Olivi e bouganville · Pantelleria
            </p>
          </div>
        </motion.div>
      </div>

      {/* Marquee strip */}
      <div className="mt-16 md:mt-28 border-y border-lava/15 py-4 md:py-5 overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap font-display text-xl sm:text-2xl md:text-3xl text-lava/55 gap-8 md:gap-12 font-extralight tracking-[-0.03em]">
          {Array.from({ length: 2 }).flatMap((_, k) =>
            [
              "Progettazione giardino",
              "Impianti di irrigazione",
              "Piante autoctone",
              "Manutenzione",
              "Fornitura piante",
              "Sopralluogo",
            ].map((t, i) => (
              <span key={`${k}-${i}`} className="flex items-center gap-8 md:gap-12">
                {t}
                <span className="w-1.5 h-1.5 bg-terracotta" />
              </span>
            ))
          )}
        </div>
      </div>
    </section>
  );
};

// ---------------- ABOUT ----------------
const About = () => {
  return (
    <section
      id="chi-sono"
      data-testid="about-section"
      className="relative py-20 md:py-32 bg-paper"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-12 grid grid-cols-12 gap-8 md:gap-12">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          variants={{ show: { transition: { staggerChildren: 0.12 } } }}
          className="col-span-12 lg:col-span-5"
        >
          <motion.div variants={fadeUp} className="flex items-center gap-3 mb-6">
            <span className="w-2 h-2 bg-terracotta" />
            <span className="text-[11px] uppercase tracking-[0.25em] text-terracotta font-mono">
              002 / Chi è Michael Guida
            </span>
          </motion.div>

          <motion.h2
            variants={fadeUp}
            data-testid="about-title"
            className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-[-0.04em] font-extralight text-lava"
          >
            Da dieci anni
            <br />
            mi prendo cura del{" "}
            <span className="font-medium text-olive">verde pantesco</span>.
          </motion.h2>

          <motion.div
            variants={fadeUp}
            className="mt-8 space-y-5 text-lava/75 leading-relaxed font-light max-w-md"
          >
            <p>
              Opero <span className="text-lava font-medium">dal 2015</span>{" "}
              sul territorio di Pantelleria, fianco a fianco con privati,
              aziende e strutture ricettive che desiderano un giardino
              autenticamente mediterraneo.
            </p>
            <p>
              La mia specialità è la progettazione di giardini con piante
              autoctone, impianti di irrigazione efficienti e la manutenzione
              della tua oasi privata — quel piccolo paradiso fra muretti a secco
              e vista mare che rende unica quest'isola.
            </p>
            <p>
              Lavoro con serietà e professionalità: pochi clienti per volta,
              attenzione massima ai dettagli, rispetto per la natura del luogo.
            </p>
          </motion.div>

          <motion.div
            variants={fadeUp}
            className="mt-10 grid grid-cols-3 gap-6 border-t border-lava/15 pt-8"
          >
            <Stat number="10+" label="Anni di esperienza" />
            <Stat number="100%" label="Pantesco" />
            <Stat number="24h" label="Risposta media" />
          </motion.div>
        </motion.div>

        {/* RIGHT - asymmetric image block */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="col-span-12 lg:col-span-7 relative"
        >
          <div className="grid grid-cols-6 grid-rows-6 gap-4 h-[520px] md:h-[640px]">
            <div className="col-span-4 row-span-4 overflow-hidden bg-sand">
              <img
                src={PHOTOS[2].url}
                alt={PHOTOS[2].alt}
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1200ms]"
              />
            </div>
            <div className="col-span-2 row-span-3 col-start-5 row-start-1 overflow-hidden bg-sand">
              <img
                src={PHOTOS[3].url}
                alt={PHOTOS[3].alt}
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1200ms]"
              />
            </div>
            <div className="col-span-3 row-span-2 col-start-5 row-start-4 bg-olive p-5 flex flex-col justify-between text-cream">
              <span className="text-[10px] uppercase tracking-[0.25em] opacity-70 font-mono">
                Stile mediterraneo
              </span>
              <p className="font-display text-xl md:text-2xl leading-[1.05] tracking-[-0.03em] font-light">
                Pietra lavica, palme, bouganville, ulivi.
              </p>
            </div>
            <div className="col-span-4 row-span-2 col-start-1 row-start-5 overflow-hidden bg-sand">
              <img
                src={PHOTOS[4].url}
                alt={PHOTOS[4].alt}
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1200ms]"
              />
            </div>
            <div className="col-span-2 row-span-1 col-start-5 row-start-6 bg-terracotta flex items-center justify-center text-cream">
              <span className="font-display text-xl md:text-2xl tracking-[-0.03em] font-light">
                est. 2015
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const Stat = ({ number, label }) => (
  <div>
    <div className="font-display text-3xl md:text-4xl text-lava font-light tracking-[-0.04em]">
      {number}
    </div>
    <div className="text-[10px] uppercase tracking-[0.2em] text-lava/55 mt-2 font-mono">
      {label}
    </div>
  </div>
);

// ---------------- SERVICES ----------------
const Services = () => {
  const { href: waHref } = useWhatsApp();
  const colorMap = {
    olive: "bg-olive text-cream",
    terracotta: "bg-terracotta text-cream",
    lava: "bg-lava text-cream",
  };
  return (
    <section
      id="servizi"
      data-testid="services-section"
      className="py-20 md:py-32 bg-cream relative"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-12">
        <div className="grid grid-cols-12 gap-8 mb-14 md:mb-20">
          <div className="col-span-12 md:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-2 h-2 bg-olive" />
              <span className="text-[11px] uppercase tracking-[0.25em] text-olive font-mono">
                003 / Servizi
              </span>
            </div>
            <h2
              data-testid="services-title"
              className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-[-0.04em] font-extralight text-lava"
            >
              Cosa posso{" "}
              <span className="font-medium text-terracotta">fare per te</span>.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-6 md:col-start-7 self-end">
            <p className="text-lava/75 leading-relaxed font-light">
              Sei privato, azienda o stai costruendo una struttura ricettiva?
              Scegli il servizio che fa per te. Per ogni progetto fornisco un
              preventivo chiaro e personalizzato.
            </p>
          </div>
        </div>

        {/* HERO SERVICE — Progettazione giardini (featured) */}
        {(() => {
          const featured = SERVICES[0];
          const FeaturedIcon = featured.icon;
          return (
            <motion.article
              key={featured.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`service-card-${featured.id}`}
              className="group relative bg-lava text-cream p-8 md:p-14 mb-4 md:mb-6 overflow-hidden grid grid-cols-12 gap-6 md:gap-10"
            >
              {/* Decorative accent stripe */}
              <span className="absolute top-0 left-0 right-0 h-1 bg-terracotta" aria-hidden />

              <div className="col-span-12 md:col-span-7 flex flex-col justify-between min-h-[260px] md:min-h-[320px]">
                <div className="flex items-center justify-between md:justify-start gap-4">
                  <span className="inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 bg-terracotta text-cream">
                    <FeaturedIcon className="w-6 h-6 md:w-7 md:h-7" strokeWidth={1.3} />
                  </span>
                  <span className="font-mono text-[11px] tracking-[0.2em] text-cream/55 uppercase">
                    Servizio principale · 01 / 06
                  </span>
                </div>

                <div className="mt-10 md:mt-0">
                  <h3 className="font-display text-4xl sm:text-5xl md:text-6xl leading-[0.95] tracking-[-0.04em] font-light">
                    {featured.title}
                    <span className="text-terracotta">.</span>
                  </h3>
                  <p className="mt-5 md:mt-6 text-lg md:text-xl text-cream font-light leading-snug max-w-xl">
                    {featured.desc}
                  </p>
                  <p className="mt-4 text-sm md:text-base text-cream/65 leading-relaxed font-light max-w-xl">
                    {featured.longDesc}
                  </p>
                </div>
              </div>

              <div className="col-span-12 md:col-span-5 flex flex-col gap-3 md:items-end justify-end">
                <a
                  href="#preventivo"
                  data-testid="featured-cta-preventivo"
                  className="inline-flex items-center gap-3 bg-cream text-lava px-6 py-4 text-sm tracking-tight font-medium hover:bg-terracotta hover:text-cream transition-colors duration-500 w-full sm:w-auto justify-center"
                >
                  Richiedi un progetto
                  <ArrowUpRight className="w-4 h-4" />
                </a>
                <a
                  href={waHref}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="featured-cta-whatsapp"
                  className="inline-flex items-center gap-3 border border-cream/30 text-cream px-6 py-4 text-sm tracking-tight font-medium hover:bg-cream hover:text-lava transition-colors duration-500 w-full sm:w-auto justify-center"
                >
                  Parlane su WhatsApp
                  <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>
            </motion.article>
          );
        })()}

        {/* Altri servizi */}
        <div className="grid grid-cols-12 gap-4 md:gap-6">
          {SERVICES.slice(1).map((s, idx) => {
            const i = idx + 1;
            const Icon = s.icon;
            const span =
              idx === 0
                ? "md:col-span-6"
                : idx === 1
                ? "md:col-span-6"
                : "md:col-span-4";
            return (
              <motion.article
                key={s.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{
                  duration: 0.7,
                  delay: idx * 0.06,
                  ease: [0.22, 1, 0.36, 1],
                }}
                data-testid={`service-card-${s.id}`}
                className={`col-span-12 ${span} group relative bg-paper border border-lava/10 p-7 md:p-9 min-h-[240px] flex flex-col justify-between hover:bg-lava hover:text-cream transition-colors duration-700`}
              >
                <div className="flex items-start justify-between">
                  <span
                    className={`inline-flex items-center justify-center w-12 h-12 ${colorMap[s.accent]}`}
                  >
                    <Icon className="w-5 h-5" strokeWidth={1.4} />
                  </span>
                  <span className="font-mono text-[11px] tracking-[0.15em] text-lava/40 group-hover:text-cream/50 transition-colors">
                    0{i + 1} / 06
                  </span>
                </div>

                <div className="mt-10">
                  <h3 className="font-display text-2xl md:text-3xl leading-[1.05] tracking-[-0.03em] font-light">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm md:text-base leading-relaxed font-light text-lava/70 group-hover:text-cream/75 transition-colors">
                    {s.desc}
                  </p>
                </div>
              </motion.article>
            );
          })}
        </div>

        {/* CTA banda */}
        <div className="mt-14 md:mt-20 grid grid-cols-12 gap-6 items-center bg-lava text-cream p-8 md:p-14">
          <div className="col-span-12 md:col-span-7">
            <p className="font-display text-2xl md:text-4xl leading-[1.05] tracking-[-0.03em] font-extralight">
              Hai un progetto in mente per il tuo giardino pantesco?
            </p>
            <p className="text-cream/65 mt-4 text-sm md:text-base font-light">
              Scrivimi su WhatsApp o compila il modulo. Ti ricontatto entro 24 ore.
            </p>
          </div>
          <div className="col-span-12 md:col-span-5 md:text-right flex md:justify-end gap-3 flex-wrap">
            <a
              href={waHref}
              target="_blank"
              rel="noreferrer"
              data-testid="services-cta-whatsapp"
              className="inline-flex items-center gap-3 bg-cream text-lava px-6 py-3.5 text-sm tracking-tight font-medium hover:bg-terracotta hover:text-cream transition-colors duration-500"
            >
              WhatsApp
              <ArrowUpRight className="w-4 h-4" />
            </a>
            <a
              href="#preventivo"
              data-testid="services-cta-form"
              className="inline-flex items-center gap-3 border border-cream/30 text-cream px-6 py-3.5 text-sm tracking-tight font-medium hover:bg-cream hover:text-lava transition-colors duration-500"
            >
              Modulo
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// ---------------- GALLERY ----------------
const Gallery = () => {
  return (
    <section
      id="lavori"
      data-testid="gallery-section"
      className="py-20 md:py-32 bg-paper"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-12">
        <div className="grid grid-cols-12 gap-8 mb-14 md:mb-20">
          <div className="col-span-12 md:col-span-7">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-2 h-2 bg-bougainvillea" />
              <span className="text-[11px] uppercase tracking-[0.25em] text-bougainvillea font-mono">
                004 / Lavori realizzati
              </span>
            </div>
            <h2
              data-testid="gallery-title"
              className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-[-0.04em] font-extralight text-lava"
            >
              Una piccola parte dei{" "}
              <span className="font-medium text-olive">tanti progetti</span>
              <br />
              realizzati a Pantelleria.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9 self-end">
            <p className="text-lava/65 font-light text-base md:text-lg leading-relaxed border-l border-lava/20 pl-5">
              Ogni giardino ha la sua storia. Quella della tua isola la conosco
              da sempre.
            </p>
          </div>
        </div>

        {/* Modern bento photo grid - 7 photos */}
        <div className="grid grid-cols-12 gap-3 md:gap-4 auto-rows-[120px] md:auto-rows-[180px]">
          <GalleryItem photo={PHOTOS[1]} className="col-span-12 md:col-span-8 row-span-3" testid="gallery-img-0" />
          <GalleryItem photo={PHOTOS[2]} className="col-span-6 md:col-span-4 row-span-2" testid="gallery-img-1" />
          <GalleryItem photo={PHOTOS[3]} className="col-span-6 md:col-span-4 row-span-1" testid="gallery-img-2" />
          <GalleryItem photo={PHOTOS[0]} className="col-span-6 md:col-span-4 row-span-2" testid="gallery-img-3" />
          <GalleryItem photo={PHOTOS[4]} className="col-span-6 md:col-span-4 row-span-2" testid="gallery-img-4" />
          <GalleryItem photo={PHOTOS[5]} className="col-span-6 md:col-span-4 row-span-2" testid="gallery-img-5" />
          <GalleryItem photo={PHOTOS[6]} className="col-span-6 md:col-span-4 row-span-2" testid="gallery-img-6" />
        </div>

        <p className="mt-10 text-xs text-lava/50 max-w-2xl font-mono tracking-wide">
          * Foto di lavori reali eseguiti sul territorio pantesco. Stile
          mediterraneo, piante autoctone, integrazione con muretti a secco
          tradizionali.
        </p>
      </div>
    </section>
  );
};

const GalleryItem = ({ photo, className, testid }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.97 }}
    whileInView={{ opacity: 1, scale: 1 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
    data-testid={testid}
    className={`relative overflow-hidden bg-sand group ${className}`}
  >
    <img
      src={photo.url}
      alt={photo.alt}
      loading="lazy"
      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1400ms]"
    />
    <div className="absolute inset-0 bg-lava/0 group-hover:bg-lava/20 transition-colors duration-700" />
  </motion.div>
);

// ---------------- FAQ ----------------
const FAQ_ITEMS = [
  {
    q: "Quanto costa un giardiniere a Pantelleria?",
    a: "Il costo dipende dal tipo di intervento (manutenzione ordinaria, progettazione, irrigazione, piantumazione) e dalla dimensione del giardino. Ogni preventivo è gratuito e personalizzato: compila il modulo o scrivi su WhatsApp al 388 193 7674 e Michael ti ricontatta entro 24 ore con una stima chiara.",
  },
  {
    q: "Lavorate anche su dammusi e seconde case quando il proprietario non è sull'isola?",
    a: "Sì, è una delle nostre attività principali. Molti dei nostri clienti sono proprietari di dammusi e ville a Pantelleria che vivono fuori dall'isola: ci occupiamo della manutenzione regolare, della cura del verde durante la loro assenza e dell'apertura/chiusura stagionale del giardino. Inviamo report fotografici dopo ogni intervento.",
  },
  {
    q: "Quali piante autoctone consigliate per un giardino a Pantelleria?",
    a: "Le specie che meglio resistono al clima pantesco (vento, scirocco, salsedine, scarsa pioggia estiva) sono ulivi, capperi, palme, bouganville, fichi, oleandri, lentisco, mirto, agave, plumbago e cactacee. La nostra specialità è progettare giardini interamente con piante autoctone che richiedono poca acqua e crescono in armonia con i muretti a secco in pietra lavica.",
  },
  {
    q: "Realizzate impianti di irrigazione adatti al clima dell'isola?",
    a: "Sì. Progettiamo impianti a goccia e a microirrigazione calibrati per il clima caldo e ventoso di Pantelleria, con programmazione oraria automatica e sensori. Studiamo lo schema in base all'esposizione del terreno, alla pressione disponibile e al tipo di piante, per minimizzare il consumo d'acqua mantenendo il giardino rigoglioso anche d'estate.",
  },
  {
    q: "In che zone di Pantelleria operate?",
    a: "Operiamo su tutta l'isola di Pantelleria: Centro, Khamma, Tracino, Bukkuram, Scauri, Rekale, Sibà, Mursia, Campobello, Bugeber, Sciuvechi e tutte le contrade. Per i preventivi facciamo sopralluogo direttamente in loco.",
  },
  {
    q: "Quanto tempo serve per progettare e realizzare un giardino?",
    a: "Per un progetto su misura servono in genere 2-4 settimane dal sopralluogo alla consegna del piano; la realizzazione vera e propria dipende dalle dimensioni e dalle piante scelte, ma in media va da 1 settimana (giardini piccoli) a 2-3 mesi (giardini ampi con muretti e irrigazione). Definiamo tempi precisi nel preventivo.",
  },
  {
    q: "Fornite anche le piante o devo comprarle io?",
    a: "Forniamo direttamente le piante. Selezioniamo esemplari adulti e giovani da vivai specializzati in specie mediterranee e li consegniamo già pronti per la piantumazione. Per progetti grandi possiamo gestire l'intera fornitura, risparmiandoti il trasporto via traghetto.",
  },
  {
    q: "Come posso richiedere un preventivo o contattarvi?",
    a: "Puoi compilare il modulo qui sul sito (sezione Preventivo) e ti ricontattiamo entro 24 ore, oppure scriverci direttamente su WhatsApp al 388 193 7674. Per richieste formali via email scrivi a giovannimichael94@hotmail.it.",
  },
];

const FAQ = () => {
  const [open, setOpen] = useState(0);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="py-20 md:py-32 bg-cream relative"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-12 grid grid-cols-12 gap-8 md:gap-14">
        <div className="col-span-12 lg:col-span-5">
          <div className="flex items-center gap-3 mb-5 sticky top-24">
            <span className="w-2 h-2 bg-olive" />
            <span className="text-[11px] uppercase tracking-[0.25em] text-olive font-mono">
              005 / Domande frequenti
            </span>
          </div>
          <h2
            data-testid="faq-title"
            className="font-display text-4xl sm:text-5xl md:text-6xl leading-[0.95] tracking-[-0.04em] font-extralight text-lava"
          >
            Le domande che
            <br />
            <span className="font-medium text-terracotta">ci fanno più spesso</span>.
          </h2>
          <p className="mt-7 text-lava/70 leading-relaxed font-light max-w-md">
            Se hai una domanda diversa, scrivici direttamente: Michael
            risponde personalmente entro 24 ore.
          </p>
        </div>

        <div className="col-span-12 lg:col-span-7">
          <div className="border-t border-lava/15">
            {FAQ_ITEMS.map((item, i) => {
              const isOpen = open === i;
              return (
                <div
                  key={i}
                  data-testid={`faq-item-${i}`}
                  className="border-b border-lava/15"
                >
                  <button
                    type="button"
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    data-testid={`faq-trigger-${i}`}
                    aria-expanded={isOpen}
                    className="w-full flex items-start justify-between gap-6 py-6 md:py-7 text-left group"
                  >
                    <span className="flex items-start gap-4 md:gap-5">
                      <span className="font-mono text-[11px] tracking-[0.15em] text-lava/40 mt-1.5 shrink-0">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="font-display text-lg md:text-2xl tracking-[-0.02em] font-medium text-lava group-hover:text-olive transition-colors leading-snug">
                        {item.q}
                      </span>
                    </span>
                    <span
                      className={`shrink-0 w-8 h-8 border border-lava/20 flex items-center justify-center mt-1 transition-all duration-500 ${
                        isOpen ? "bg-olive border-olive text-cream rotate-45" : "text-lava"
                      }`}
                      aria-hidden
                    >
                      <span className="text-lg leading-none">+</span>
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                      >
                        <div
                          data-testid={`faq-answer-${i}`}
                          className="pb-7 pl-9 md:pl-12 pr-12 text-base text-lava/75 leading-relaxed font-light max-w-2xl"
                        >
                          {item.a}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

// ---------------- CONTACT FORM ----------------
const ContactForm = () => {
  const { setService } = useWhatsApp();
  const [form, setForm] = useState({
    nome: "",
    telefono: "",
    email: "",
    servizio: "",
    dettagli_altro: "",
    messaggio: "",
    localita: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => {
    const value = typeof e === "string" ? e : e.target.value;
    setForm((p) => ({ ...p, [k]: value }));
    if (k === "servizio") setService(value);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.telefono || !form.email || !form.servizio) {
      toast.error("Compila tutti i campi obbligatori.");
      return;
    }
    if (form.servizio === "altro" && !form.dettagli_altro.trim()) {
      toast.error("Specifica il servizio nel campo 'Altro'.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.dettagli_altro) delete payload.dettagli_altro;
      if (!payload.messaggio) delete payload.messaggio;
      if (!payload.localita) delete payload.localita;
      await axios.post(`${API}/quotes`, payload);
      toast.success("Richiesta inviata!", {
        description: "Ti ricontatto entro 24 ore.",
      });
      setSubmitted(true);
      setForm({
        nome: "",
        telefono: "",
        email: "",
        servizio: "",
        dettagli_altro: "",
        messaggio: "",
        localita: "",
      });
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.detail ||
        "Si è verificato un errore. Riprova o scrivimi su WhatsApp.";
      toast.error(typeof msg === "string" ? msg : "Errore invio richiesta.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="preventivo"
      data-testid="quote-section"
      className="py-20 md:py-32 bg-cream relative"
    >
      <div className="max-w-3xl mx-auto px-5 md:px-8">
        <div className="text-center mb-12 md:mb-16">
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="w-2 h-2 bg-terracotta" />
            <span className="text-[11px] uppercase tracking-[0.25em] text-terracotta font-mono">
              006 / Richiedi preventivo
            </span>
            <span className="w-2 h-2 bg-terracotta" />
          </div>
          <h2
            data-testid="contact-title"
            className="font-display text-4xl sm:text-5xl md:text-6xl leading-[0.95] tracking-[-0.04em] font-extralight text-lava"
          >
            Parliamone.
            <br />
            <span className="font-medium text-olive">Risposta entro 24h.</span>
          </h2>
          <p className="mt-6 md:mt-7 text-lava/70 leading-relaxed font-light max-w-xl mx-auto text-base">
            Compila il modulo qui sotto scegliendo il servizio che ti interessa.
            Ogni preventivo è gratuito e senza impegno.
          </p>
        </div>

        <form
          onSubmit={submit}
          data-testid="contact-form"
          className="bg-paper border border-lava/10 p-6 sm:p-8 md:p-12 space-y-7"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label="Nome e cognome *" htmlFor="nome">
              <Input
                id="nome"
                data-testid="form-input-nome"
                value={form.nome}
                onChange={update("nome")}
                required
                className="rounded-none border-0 border-b border-lava/20 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-olive text-base h-11"
                placeholder="Mario Rossi"
              />
            </Field>
            <Field label="Telefono *" htmlFor="telefono">
              <Input
                id="telefono"
                type="tel"
                inputMode="tel"
                data-testid="form-input-telefono"
                value={form.telefono}
                onChange={update("telefono")}
                required
                className="rounded-none border-0 border-b border-lava/20 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-olive text-base h-11"
                placeholder="+39 333 1234567"
              />
            </Field>
            <Field label="Email *" htmlFor="email">
              <Input
                id="email"
                type="email"
                inputMode="email"
                data-testid="form-input-email"
                value={form.email}
                onChange={update("email")}
                required
                className="rounded-none border-0 border-b border-lava/20 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-olive text-base h-11"
                placeholder="tu@email.it"
              />
            </Field>
            <Field label="Località a Pantelleria" htmlFor="localita">
              <Input
                id="localita"
                data-testid="form-input-localita"
                value={form.localita}
                onChange={update("localita")}
                className="rounded-none border-0 border-b border-lava/20 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-olive text-base h-11"
                placeholder="Es. Khamma, Scauri…"
              />
            </Field>
          </div>

          <Field label="Servizio richiesto *" htmlFor="servizio">
            <Select
              value={form.servizio}
              onValueChange={update("servizio")}
            >
              <SelectTrigger
                id="servizio"
                data-testid="form-select-servizio"
                className="rounded-none border-0 border-b border-lava/20 bg-transparent px-0 focus:ring-0 focus:border-olive text-base h-11"
              >
                <SelectValue placeholder="Scegli un servizio…" />
              </SelectTrigger>
              <SelectContent
                data-testid="form-select-content"
                className="rounded-none border-lava/15 bg-cream"
              >
                {Object.entries(SERVICE_LABELS).map(([k, v]) => (
                  <SelectItem
                    key={k}
                    value={k}
                    data-testid={`form-select-option-${k}`}
                    className="rounded-none font-sans cursor-pointer text-base py-3"
                  >
                    {v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <AnimatePresence initial={false}>
            {form.servizio === "altro" && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.4 }}
              >
                <Field
                  label="Specifica il servizio richiesto *"
                  htmlFor="dettagli_altro"
                >
                  <Textarea
                    id="dettagli_altro"
                    data-testid="form-input-dettagli-altro"
                    value={form.dettagli_altro}
                    onChange={update("dettagli_altro")}
                    rows={3}
                    className="rounded-none border border-lava/20 bg-transparent focus-visible:ring-0 focus-visible:border-olive text-base resize-none"
                    placeholder="Descrivi cosa ti serve…"
                  />
                </Field>
              </motion.div>
            )}
          </AnimatePresence>

          <Field label="Messaggio (opzionale)" htmlFor="messaggio">
            <Textarea
              id="messaggio"
              data-testid="form-input-messaggio"
              value={form.messaggio}
              onChange={update("messaggio")}
              rows={4}
              className="rounded-none border border-lava/20 bg-transparent focus-visible:ring-0 focus-visible:border-olive text-base resize-none"
              placeholder="Raccontami il tuo progetto, lo spazio, le piante che immagini…"
            />
          </Field>

          <div className="flex flex-col gap-4 pt-2">
            <Button
              type="submit"
              data-testid="form-submit-btn"
              disabled={submitting}
              className="rounded-none bg-lava text-cream hover:bg-olive transition-colors duration-500 px-8 h-14 text-sm tracking-tight font-medium w-full sm:w-auto"
            >
              {submitting ? "Invio in corso…" : "Invia richiesta"}
              <ArrowUpRight className="w-4 h-4 ml-2" />
            </Button>
            <p className="text-xs text-lava/55 leading-relaxed">
              Ti ricontatto entro 24 ore. I tuoi dati restano riservati e
              vengono usati solo per rispondere a questa richiesta.
            </p>
          </div>

          {submitted && (
            <p
              data-testid="form-success-msg"
              className="text-sm text-olive border-t border-olive/30 pt-4"
            >
              Grazie! La tua richiesta è stata ricevuta. Ti ricontatto presto.
            </p>
          )}
        </form>
      </div>
    </section>
  );
};

// ---------------- DIVIDER ----------------
const SectionDivider = () => (
  <div
    aria-hidden
    data-testid="section-divider"
    className="bg-cream py-10 md:py-14"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center gap-5">
      <span className="h-px flex-1 bg-lava/15" />
      <span className="text-[10px] uppercase tracking-[0.3em] text-lava/45 font-mono whitespace-nowrap">
        oppure / contatto diretto
      </span>
      <span className="h-px flex-1 bg-lava/15" />
    </div>
  </div>
);

// ---------------- DIRECT CONTACT ----------------
const DirectContact = () => {
  const { href: waHref } = useWhatsApp();
  return (
    <section
      id="contatti"
      data-testid="contact-section"
      className="py-20 md:py-32 bg-lava text-cream relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-12 grid grid-cols-12 gap-10 md:gap-14">
        <div className="col-span-12 lg:col-span-6">
          <div className="flex items-center gap-3 mb-5">
            <span className="w-2 h-2 bg-terracotta" />
            <span className="text-[11px] uppercase tracking-[0.25em] text-terracotta font-mono">
              007 / Contatto diretto
            </span>
          </div>
          <h2
            data-testid="direct-contact-title"
            className="font-display text-4xl sm:text-5xl md:text-6xl leading-[0.95] tracking-[-0.04em] font-extralight"
          >
            Preferisci una
            <br />
            <span className="font-medium text-terracotta">chiamata veloce?</span>
          </h2>
          <p className="mt-6 md:mt-7 text-cream/70 leading-relaxed font-light max-w-md text-base">
            Scrivimi su WhatsApp o telefonami direttamente. Sono in giro per
            l'isola tutto il giorno — risposta rapida garantita.
          </p>

          <a
            href={waHref}
            target="_blank"
            rel="noreferrer"
            data-testid="direct-whatsapp-btn"
            className="mt-8 md:mt-10 inline-flex items-center gap-4 bg-olive hover:bg-olive-deep text-cream pl-5 pr-7 py-5 transition-colors duration-500 group w-full sm:w-auto justify-center sm:justify-start"
          >
            <span className="w-10 h-10 bg-cream/15 flex items-center justify-center">
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zm-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884zm8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
            </span>
            <span className="flex flex-col items-start leading-tight">
              <span className="text-[10px] uppercase tracking-[0.25em] opacity-70 font-mono">
                Scrivimi su
              </span>
              <span className="font-display text-xl md:text-2xl font-medium tracking-tight">
                WhatsApp
              </span>
            </span>
            <ArrowUpRight className="w-5 h-5 ml-auto sm:ml-3" />
          </a>
        </div>

        <div className="col-span-12 lg:col-span-6 lg:border-l lg:border-cream/15 lg:pl-12">
          <p className="text-[10px] uppercase tracking-[0.25em] text-cream/50 mb-7 font-mono">
            · Oppure raggiungimi qui
          </p>
          <div className="space-y-6">
            <DirectLine
              icon={<Phone className="w-4 h-4" />}
              label="Telefono / WhatsApp"
              value={WHATSAPP_NUMBER_DISPLAY}
              href={waHref}
              testid="contact-line-phone"
            />
            <DirectLine
              icon={<Mail className="w-4 h-4" />}
              label="Email"
              value={EMAIL_CONTACT}
              href={`mailto:${EMAIL_CONTACT}`}
              testid="contact-line-email"
            />
            <DirectLine
              icon={<MapPin className="w-4 h-4" />}
              label="Zona di lavoro"
              value="Isola di Pantelleria (TP)"
              testid="contact-line-location"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, htmlFor, children }) => (
  <div className="space-y-2">
    <Label
      htmlFor={htmlFor}
      className="text-[10px] uppercase tracking-[0.2em] text-lava/55 font-mono font-normal"
    >
      {label}
    </Label>
    {children}
  </div>
);

const DirectLine = ({ icon, label, value, href, testid }) => {
  const content = (
    <div className="flex items-start gap-4 group">
      <span className="w-10 h-10 bg-cream/10 text-cream flex items-center justify-center mt-0.5 group-hover:bg-terracotta group-hover:text-cream transition-colors duration-500 border border-cream/15">
        {icon}
      </span>
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-[0.2em] text-cream/45 font-mono">
          {label}
        </div>
        <div className="font-display text-base md:text-lg text-cream font-medium tracking-tight mt-1 break-words">
          {value}
        </div>
      </div>
      {href && (
        <ArrowUpRight className="w-4 h-4 text-cream/40 group-hover:text-cream transition-colors" />
      )}
    </div>
  );
  return href ? (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      data-testid={testid}
      className="block py-3 border-b border-cream/10 hover:border-cream/30 transition-colors"
    >
      {content}
    </a>
  ) : (
    <div data-testid={testid} className="py-3 border-b border-cream/10">
      {content}
    </div>
  );
};

// ---------------- WHATSAPP FLOATING ----------------
const WhatsAppFloating = () => {
  const [visible, setVisible] = useState(false);
  const [atFooter, setAtFooter] = useState(false);
  const { href: waHref } = useWhatsApp();

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 300);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const footer = document.querySelector('[data-testid="footer"]');
    if (!footer) return;
    const io = new IntersectionObserver(
      ([entry]) => setAtFooter(entry.isIntersecting && entry.intersectionRatio > 0.25),
      { threshold: [0, 0.25, 0.5] }
    );
    io.observe(footer);
    return () => io.disconnect();
  }, []);

  const show = visible && !atFooter;
  return (
    <AnimatePresence>
      {show && (
        <motion.a
          initial={{ opacity: 0, y: 20, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.9 }}
          href={waHref}
          target="_blank"
          rel="noreferrer"
          data-testid="whatsapp-floating"
          className="fixed bottom-6 right-6 z-50 inline-flex items-center gap-3 bg-olive text-cream pl-4 pr-5 py-3.5 shadow-[0_8px_30px_rgba(28,27,26,0.25)] hover:bg-olive-deep transition-colors duration-500 group"
          aria-label="Scrivimi su WhatsApp"
        >
          <span className="w-8 h-8 bg-cream/15 rounded-full flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zm-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884zm8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
          </span>
          <span className="font-sans text-sm tracking-wide">Scrivimi</span>
        </motion.a>
      )}
    </AnimatePresence>
  );
};

// ---------------- FOOTER ----------------
const Footer = () => {
  const { href: waHref } = useWhatsApp();
  return (
    <footer
      data-testid="footer"
      className="bg-lava text-cream py-14 md:py-24 pb-32 md:pb-32 relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-12">
        <div className="grid grid-cols-12 gap-8 md:gap-12">
          <div className="col-span-12 md:col-span-6">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-10 bg-cream text-lava flex items-center justify-center font-display text-base font-medium tracking-tight">
                MG
              </span>
              <div className="leading-none">
                <p className="font-display text-xl font-medium tracking-tight">
                  Michael Guida
                </p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-cream/55 mt-2 font-mono">
                  Giardiniere / Pantelleria / dal 2015
                </p>
              </div>
            </div>
            <p className="font-display text-3xl md:text-5xl leading-[0.95] tracking-[-0.04em] max-w-xl text-cream font-extralight">
              Giardiniere
              <br />
              <span className="font-medium">Pantelleria.</span>
            </p>
            <p className="text-cream/55 mt-5 text-sm md:text-base font-light max-w-md">
              Risposta entro 24 ore. Preventivi gratuiti.
            </p>
          </div>

          <div className="col-span-6 md:col-span-3">
            <p className="text-[10px] uppercase tracking-[0.2em] text-cream/45 mb-5 font-mono">
              · Contatti
            </p>
            <ul className="space-y-3 text-sm">
              <li>
                <a
                  href={waHref}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-whatsapp"
                  className="editorial-link text-cream/85 hover:text-cream"
                >
                  WhatsApp · {WHATSAPP_NUMBER_DISPLAY}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${EMAIL_CONTACT}`}
                  data-testid="footer-email"
                  className="editorial-link text-cream/85 hover:text-cream"
                >
                  {EMAIL_CONTACT}
                </a>
              </li>
              <li className="text-cream/85">Isola di Pantelleria (TP)</li>
            </ul>
          </div>

          <div className="col-span-6 md:col-span-3">
            <p className="text-[10px] uppercase tracking-[0.2em] text-cream/45 mb-5 font-mono">
              · Servizi
            </p>
            <ul className="space-y-3 text-sm text-cream/85">
              {[
                { id: "progettazione_giardino", label: "Progettazione giardino" },
                { id: "impianti_irrigazione", label: "Impianti di irrigazione" },
                { id: "piantumazione_autoctone", label: "Piante autoctone" },
                { id: "manutenzione_giardino", label: "Manutenzione" },
                { id: "fornitura_piante", label: "Fornitura piante" },
                { id: "consulenza_sopralluogo", label: "Sopralluogo" },
              ].map((s) => (
                <li key={s.id}>
                  <a
                    href={buildWhatsAppHref(s.id)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`footer-service-${s.id}`}
                    className="editorial-link inline-flex items-center gap-2 text-cream/85 hover:text-cream group"
                  >
                    {s.label}
                    <ArrowUpRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                  </a>
                </li>
              ))}
            </ul>
            <p className="mt-5 text-[10px] uppercase tracking-[0.2em] text-cream/35 font-mono leading-relaxed">
              · Click = WhatsApp con messaggio pronto
            </p>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-cream/15 flex flex-col md:flex-row gap-5 md:gap-4 md:items-center justify-between text-[10px] uppercase tracking-[0.2em] text-cream/50 font-mono">
          <span>© {new Date().getFullYear()} Pantelflora S.N.C. — P.IVA IT01656050810</span>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            <Link
              to="/privacy"
              data-testid="footer-link-privacy"
              className="hover:text-cream transition-colors"
            >
              Privacy Policy
            </Link>
            <Link
              to="/cookie-policy"
              data-testid="footer-link-cookie"
              className="hover:text-cream transition-colors"
            >
              Cookie Policy
            </Link>
            <button
              type="button"
              data-testid="footer-link-cookie-prefs"
              onClick={() => window.dispatchEvent(new CustomEvent("open-cookie-banner"))}
              className="hover:text-cream transition-colors uppercase tracking-[0.2em] cursor-pointer"
            >
              Preferenze cookie
            </button>
            <span className="opacity-70" data-testid="footer-credit">
              Site by Pantelflora S.N.C.
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ---------------- LANDING ----------------
const Landing = () => {
  const [service, setService] = useState("");
  const ctxValue = useMemo(
    () => ({
      service,
      setService,
      href: buildWhatsAppHref(service),
    }),
    [service]
  );

  return (
    <WhatsAppCtx.Provider value={ctxValue}>
      <main className="bg-cream text-lava">
        <Nav />
        <Hero />
        <About />
        <Services />
        <Gallery />
        <FAQ />
        <ContactForm />
        <SectionDivider />
        <DirectContact />
        <Footer />
        <WhatsAppFloating />
      </main>
    </WhatsAppCtx.Provider>
  );
};

export default Landing;
